import Button from '@rio-cloud/rio-uikit/Button';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import { type ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { useGetProductDetailsQuery } from '../../api/product/productApiSlice';
import type { ApiAssetInfo } from '../../api/tags/tag.types';
import { isBookableProduct, isMultiLevelProduct, type Product } from '../../checkout/redux/types';
import { type DiscountIdentifier, mapStateToFindDiscountProps } from '../../common/discount/DiscountService';
import type { AssetDiscount } from '../../common/discount/redux/types';
import { SUBSCRIPTION_WRITE } from '../../common/permissions/permissions';
import { getPermissions } from '../../common/permissions/redux/permissions.redux';
import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import { getDefaultBookableLevel, isRatePlanBookable } from '../../common/product/productService';
import { ProductIcon } from '../../common/productImages/ProductIcon';
import { termedLevelRenewalActions } from '../../common/termedLevel/redux/termedLevelRenewal.redux';
import type { TermedLevel } from '../../common/termedLevel/types';
import { mapResourceTypeToProductType } from '../contracts/mapResourceTypeToProductType';
import { openCheckoutPopup } from '../serviceOverview/services/checkoutService';
import { GtmPromotion } from '../serviceOverview/services/gtmPromotion';
import { BookingOverviewContractSidebar } from './BookingOverviewContractSidebar';
import { BookingOverviewContractTable } from './BookingOverviewContractTable';
import type { BookingOverviewColumn, BookingOverviewGrouping } from './bookingOverviewColumns';

interface OwnProps {
    contracts: TermedLevel[];
    filteredContracts?: TermedLevel[];
    grouping?: BookingOverviewGrouping;
    visibleColumns?: BookingOverviewColumn[];
    products: Product[];
    assets: ApiAssetInfo[];
}

interface Props extends OwnProps {
    canConfigureRenewal: boolean;
    findDiscount: (identifier: DiscountIdentifier) => AssetDiscount | undefined;
    configureRenewal: (contract: TermedLevel, contracts: TermedLevel[]) => void;
}

function groupContracts(contracts: TermedLevel[], grouping: BookingOverviewGrouping): TermedLevel[][] {
    return Array.from(
        contracts
            .reduce((groups, contract) => {
                const key =
                    grouping === 'services'
                        ? contract.productId
                        : JSON.stringify([contract.resourceType, contract.resourceId]);
                const productContracts = groups.get(key) ?? [];
                productContracts.push(contract);
                groups.set(key, productContracts);
                return groups;
            }, new Map<string, TermedLevel[]>())
            .values()
    ).sort(([left], [right]) =>
        grouping === 'services'
            ? left.productName.localeCompare(right.productName)
            : (left.resourceName || left.resourceId).localeCompare(right.resourceName || right.resourceId)
    );
}

function getCheckoutTarget(product: Product | undefined): { level?: string } | undefined {
    if (isMultiLevelProduct(product)) {
        const bookableLevels = product.levels.filter(isRatePlanBookable).map(({ level }) => level);
        const level = getDefaultBookableLevel(bookableLevels);
        return level ? { level } : undefined;
    }

    return isBookableProduct(product)
        ? { level: getDefaultBookableLevel(product.level ? [product.level] : []) }
        : undefined;
}

function ProductPanelHeader({ contracts }: { contracts: TermedLevel[] }) {
    const { productId, productName, resourceType } = contracts[0];
    const productType = mapResourceTypeToProductType(resourceType);

    return (
        <div className='display-flex align-items-center gap-10'>
            <ProductIcon productId={productId} className='width-25 height-25' />
            <div className='display-flex align-items-baseline gap-5'>
                <span className='text-size-20'>{productName}</span>
                <span className='text-size-14 text-light'>
                    <ProductTypedFormattedMessage
                        productType={productType}
                        assetBasedId='intl-msg:marketplace.bookings.overview.product.asset.count'
                        userBasedId='intl-msg:marketplace.bookings.overview.product.user.count'
                        values={{
                            count: contracts.length,
                            strong: chunks => <strong className='text-size-16'>{chunks}</strong>,
                        }}
                    />
                </span>
            </div>
        </div>
    );
}

interface BookNowFooterProps {
    canBook: boolean;
    product: Product | undefined;
    productId: string;
}

function BookNowFooter({ canBook, product, productId }: BookNowFooterProps) {
    const checkoutTarget = getCheckoutTarget(product);
    const isDisabled = !canBook || !checkoutTarget;
    const buttonContent = <FormattedMessage id='intl-msg:marketplace.buybutton.bookNow' />;

    return (
        <div className='panel-footer bg-lightest display-flex justify-content-end'>
            {isDisabled ? (
                <Button className='btn btn-primary' iconName='icon-shopping-cart' disabled>
                    {buttonContent}
                </Button>
            ) : (
                <BookNowLink productId={productId} level={checkoutTarget.level}>
                    {buttonContent}
                </BookNowLink>
            )}
        </div>
    );
}

function BookNowLink({ productId, level, children }: { productId: string; level?: string; children: ReactNode }) {
    const { callback, checkoutUrl } = openCheckoutPopup(productId, GtmPromotion.customerCenterAddMore, level);

    return (
        <Button as='a' className='btn btn-primary' iconName='icon-shopping-cart' href={checkoutUrl} onClick={callback}>
            {children}
        </Button>
    );
}

function ProductDetailsQuery({ productId, isOpen }: { productId: string; isOpen: boolean }) {
    useGetProductDetailsQuery(productId, { skip: !isOpen });
    return null;
}

export function BookingOverviewProductList({
    contracts,
    filteredContracts = contracts,
    grouping = 'services',
    visibleColumns,
    products,
    assets,
    canConfigureRenewal,
    findDiscount,
    configureRenewal,
}: Props) {
    const groupedContracts = groupContracts(filteredContracts, grouping);
    const legacyLevelsByProduct = new Map(
        contracts.map(contract => {
            const product = products.find(product => product.sku.toLowerCase() === contract.productId.toLowerCase());
            return [
                contract.productId,
                new Set(
                    isMultiLevelProduct(product)
                        ? product.levels.filter(level => level.legacy).map(level => level.level)
                        : []
                ),
            ] as const;
        })
    );
    const [selectedContractIdentity, setSelectedContract] = useState<Pick<TermedLevel, 'productId' | 'resourceId'>>();
    const selectedContract = contracts.find(
        contract =>
            contract.productId === selectedContractIdentity?.productId &&
            contract.resourceId === selectedContractIdentity?.resourceId
    );
    const selectedProduct = products.find(
        product => product.sku.toLowerCase() === selectedContract?.productId.toLowerCase()
    );
    const selectedAssetType = assets.find(asset => asset.id === selectedContract?.resourceId)?.type;

    return (
        <div>
            {groupedContracts.map((productContracts, index) => {
                const productId = productContracts[0].productId;
                const productType = mapResourceTypeToProductType(productContracts[0].resourceType);
                const product = products.find(({ sku }) => sku.toLowerCase() === productId.toLowerCase());
                const productIds = [...new Set(productContracts.map(contract => contract.productId))];
                const resource = productContracts[0];

                return (
                    <ExpanderPanel
                        key={
                            grouping === 'services'
                                ? productId
                                : JSON.stringify([resource.resourceType, resource.resourceId])
                        }
                        title={
                            grouping === 'services' ? (
                                <ProductPanelHeader contracts={productContracts} />
                            ) : (
                                <div className='display-flex align-items-center gap-10'>
                                    <span
                                        className={`uikit-icon ${resource.resourceType === 'user' ? 'icon-user' : 'icon-truck'}`}
                                        aria-hidden={true}
                                    />
                                    <span className='text-size-20'>{resource.resourceName || resource.resourceId}</span>
                                    <span className='text-light'>
                                        {resource.resourceLicensePlate || resource.resourceVin}
                                    </span>
                                </div>
                            )
                        }
                        bsStyle='default'
                        headerClassName='padding-0 padding-right-10'
                        titleClassName='flex-1-1 padding-left-10 padding-y-10'
                        bodyClassName='padding-0'
                        className='shadow-default margin-bottom-10'
                        open={index === 0}
                    >
                        {isOpen => (
                            <>
                                {productIds.map(id => (
                                    <ProductDetailsQuery key={id} productId={id} isOpen={isOpen} />
                                ))}
                                <div className='border border-top-only border-color-lighter'>
                                    <BookingOverviewContractTable
                                        contracts={productContracts}
                                        productType={productType}
                                        canConfigureRenewal={canConfigureRenewal}
                                        findDiscount={findDiscount}
                                        legacyLevels={legacyLevelsByProduct.get(productId) ?? new Set<string>()}
                                        legacyLevelsByProduct={legacyLevelsByProduct}
                                        grouping={grouping}
                                        visibleColumns={visibleColumns}
                                        onConfigureRenewal={contract => configureRenewal(contract, contracts)}
                                        onSelectContract={setSelectedContract}
                                    />
                                    {grouping === 'services' && (
                                        <BookNowFooter
                                            canBook={canConfigureRenewal}
                                            product={product}
                                            productId={productId}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </ExpanderPanel>
                );
            })}
            <BookingOverviewContractSidebar
                contract={selectedContract}
                product={selectedProduct}
                assetType={selectedAssetType}
                canConfigureRenewal={canConfigureRenewal}
                onConfigureRenewal={contract => configureRenewal(contract, contracts)}
                onClose={() => setSelectedContract(undefined)}
            />
        </div>
    );
}

const mapStateToProps = (state: RootState) => ({
    canConfigureRenewal: getPermissions(state)?.includes(SUBSCRIPTION_WRITE) ?? false,
    ...mapStateToFindDiscountProps(state),
});

const mapDispatchToProps = (dispatch: RootDispatch) => ({
    configureRenewal: (contract: TermedLevel, contracts: TermedLevel[]) => {
        dispatch(termedLevelRenewalActions.setTermedLevels(contracts));
        dispatch(
            termedLevelRenewalActions.selectForUpdate({
                contracts: [{ productId: contract.productId, resourceId: contract.resourceId }],
                renewsToLevel: contract.renewsToLevel,
                productId: contract.productId,
            })
        );
    },
});

export const BookingOverviewProductListContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(BookingOverviewProductList);
